/**
 * The paper around the pictures (asked for 2026-10-10): a colour, a pattern in
 * a colour of its own, or the person's own words written over and over
 * ("Happy Birthday Eva · Happy Birthday Eva · ..."). It fills the paper
 * outside the zones - around and between the shapes - and never a zone
 * itself, empty or not (the owner's choice); card-view.tsx masks the zones out.
 *
 * Drawn in PAGE millimetres - the sheet as printed - so the printed sheet, the
 * folded views (each piece maps page -> view) and the PDF all show the same
 * paper, folded the way real paper folds. The shapes are scattered at every
 * angle, so no fold turns them upside down; only the words have a direction
 * (the owner: "we work later if needed to make it oriented properly").
 *
 * <PaperDefs> goes once in an SVG's <defs>; <PaperFill> fills the sheet's
 * rectangle with it, wherever the caller puts it (its user space carries the
 * pattern, so a piece's transform moves the pattern with it).
 */
import type { PaperStyle } from '@foldmycard/scene';
import { measure } from './measure.ts';

// stripes, gingham, flowers and bubbles, and more colours (2026-10-18: "provide more variety")
export const PAPER_PATTERNS: PaperStyle['pattern'][] = ['plain', 'dots', 'confetti', 'hearts', 'stars', 'snow', 'leaves',
  'stripes', 'checks', 'flowers', 'bubbles', 'words'];
export const PAPER_COLOURS = ['#ffffff', '#fff7e6', '#fde4ec', '#ffe3d3', '#fff3c4', '#e5f6ec', '#d9f2ef', '#e3f1fb', '#efe7fb',
  '#e9dcc6', '#f2f2f2', '#0d2a4f', '#1f2a44', '#14432d', '#4a1f3d', '#7a1f2b'];
export const INK_COLOURS = ['#d4a017', '#d64550', '#e76fa6', '#ff8a3d', '#3f7fd9', '#00a3a3', '#2e9e5b', '#8a5cc7',
  '#9aa3ad', '#c0c4c8', '#ffffff', '#1b2430'];
export const DEFAULT_PAPER: PaperStyle = { pattern: 'plain', color: '#ffffff', ink: '#d4a017' };
const WORDS_FONT = "'Caveat', 'Noto Naskh Arabic', 'Noto Sans Hebrew', cursive";

/** Whether the paper differs from plain white at all. */
export const hasPaper = (p: PaperStyle | undefined): p is PaperStyle =>
  !!p && (p.pattern !== 'plain' || p.color.toLowerCase() !== '#ffffff');

/** A small repeatable random sequence: the same paper every time it is drawn. */
function seeded(seed: number) {
  let s = seed >>> 0;
  return () => ((s = (s * 1664525 + 1013904223) >>> 0) / 4294967296);
}

const HEART = 'M0 1.6 C-2.6 -0.4 -1.6 -2.8 0 -1.4 C1.6 -2.8 2.6 -0.4 0 1.6 Z';
/** A maple-ish leaf, pointing up, about 6 mm long. */
const LEAF = 'M0 -3.2 L0.8 -1.6 L2.2 -2.1 L1.7 -0.5 L2.9 0.2 L1.2 0.9 L1.4 2.2 L0 1.5 L-1.4 2.2 L-1.2 0.9 L-2.9 0.2 L-1.7 -0.5 L-2.2 -2.1 L-0.8 -1.6 Z';
function star(r: number) {
  const p: string[] = [];
  for (let i = 0; i < 10; i++) {
    const a = (Math.PI / 5) * i - Math.PI / 2, rr = i % 2 ? r * 0.45 : r;
    p.push(`${(Math.cos(a) * rr).toFixed(2)},${(Math.sin(a) * rr).toFixed(2)}`);
  }
  return `M${p.join(' L')} Z`;
}
/** A five-petalled flower, about 6 mm across: petals in the ink, a light middle. */
function flower(ink: string, key: string, tr: string, o: number) {
  return (
    <g key={key} transform={tr}>
      {[0, 72, 144, 216, 288].map((a) => <ellipse key={a} cx={0} cy={-1.7} rx={1.15} ry={1.7} transform={`rotate(${a})`} fill={ink} opacity={o} />)}
      <circle r={0.9} fill="#ffffff" opacity={0.9} />
    </g>
  );
}
/** A six-armed snowflake, as strokes. */
function snowflake(r: number) {
  const d: string[] = [];
  for (let k = 0; k < 6; k++) {
    const a = (Math.PI / 3) * k, c = Math.cos(a), s = Math.sin(a);
    const at = (t: number, off: number) => [c * t - s * off, s * t + c * off].map((v) => (v * r).toFixed(2)).join(' ');
    d.push(`M0 0 L${at(1, 0)}`, `M${at(0.55, 0)} L${at(0.78, 0.2)}`, `M${at(0.55, 0)} L${at(0.78, -0.2)}`);
  }
  return d.join(' ');
}

const TILE = 70;   // mm: big enough that the repeat is not noticed

/** The pattern's shapes in one tile, scattered (and wrapped at the edges, so tiles join without seams). */
function scatter(kind: PaperStyle['pattern'], ink: string) {
  // (leaves get a seed of their own: by length alone they would fall exactly where the hearts do)
  const rnd = seeded(kind.length * 7919 + 17 + ({ leaves: 101, flowers: 211, bubbles: 307 } as Record<string, number>)[kind] || 0);
  const out: React.JSX.Element[] = [];
  const count = ({ confetti: 46, hearts: 16, stars: 16, snow: 12, leaves: 14, flowers: 15, bubbles: 22 } as Record<string, number>)[kind] ?? 0;
  for (let i = 0; i < count; i++) {
    const x = rnd() * TILE, y = rnd() * TILE, a = rnd() * 360, k = 0.75 + rnd() * 0.6;
    for (const dx of [-TILE, 0, TILE]) {
      for (const dy of [-TILE, 0, TILE]) {
        const cx = x + dx, cy = y + dy;
        if (cx < -6 || cy < -6 || cx > TILE + 6 || cy > TILE + 6) continue;
        const tr = `translate(${cx.toFixed(2)} ${cy.toFixed(2)}) rotate(${a.toFixed(1)}) scale(${k.toFixed(2)})`;
        const key = `${i}.${dx}.${dy}`;
        if (kind === 'confetti') {
          out.push(i % 3 === 0
            ? <circle key={key} transform={tr} r={0.9} fill={ink} opacity={0.55 + (i % 4) * 0.12} />
            : <rect key={key} transform={tr} x={-0.6} y={-1.8} width={1.2} height={3.6} rx={0.5} fill={ink} opacity={0.5 + (i % 5) * 0.1} />);
        } else if (kind === 'hearts') {
          out.push(<path key={key} transform={`${tr} scale(1.3)`} d={HEART} fill={ink} />);
        } else if (kind === 'stars') {
          out.push(<path key={key} transform={tr} d={star(2.6)} fill={ink} />);
        } else if (kind === 'leaves') {
          // an autumn leaf with its stem and middle vein (the Autumn theme, 2026-10-17)
          out.push(
            <g key={key} transform={tr}>
              <path d={LEAF} fill={ink} opacity={0.55 + (i % 3) * 0.18} />
              <path d="M0 3.6 L0 -2.6" stroke={ink} strokeWidth={0.3} strokeLinecap="round" opacity={0.9} />
            </g>);
        } else if (kind === 'flowers') {
          out.push(flower(ink, key, tr, 0.6 + (i % 3) * 0.15));
        } else if (kind === 'bubbles') {
          const r = 1 + (i % 5) * 0.7;
          out.push(
            <g key={key} transform={`translate(${cx.toFixed(2)} ${cy.toFixed(2)})`}>
              <circle r={r} fill={ink} opacity={0.14} stroke={ink} strokeWidth={0.35} strokeOpacity={0.7} />
              <circle cx={-r * 0.35} cy={-r * 0.35} r={r * 0.22} fill="#ffffff" opacity={0.6} />
            </g>);
        } else if (kind === 'snow') {
          out.push(<path key={key} transform={tr} d={snowflake(3.4)} stroke={ink} strokeWidth={0.45} strokeLinecap="round" fill="none" />);
        }
      }
    }
  }
  return out;
}

/** The <pattern> for a paper, by id; nothing for plain paper. Goes inside <defs>. */
export function PaperDefs({ paper, id, scale = 1 }: { paper: PaperStyle | undefined; id: string; scale?: number }) {
  if (!hasPaper(paper) || paper.pattern === 'plain') return null;
  const ink = paper.ink || DEFAULT_PAPER.ink!;
  const zoom = scale === 1 ? undefined : `scale(${scale})`;
  if (paper.pattern === 'dots') {
    return (
      <pattern id={id} patternUnits="userSpaceOnUse" width={10} height={10} patternTransform={zoom}>
        <circle cx={2.5} cy={2.5} r={1.5} fill={ink} />
        <circle cx={7.5} cy={7.5} r={1.5} fill={ink} />
      </pattern>
    );
  }
  if (paper.pattern === 'stripes') {
    // soft diagonal stripes: the same on every fold, whichever way it turns
    return (
      <pattern id={id} patternUnits="userSpaceOnUse" width={8} height={8} patternTransform={`${zoom ?? ''} rotate(45)`.trim()}>
        <rect x={0} y={0} width={3} height={8} fill={ink} opacity={0.55} />
      </pattern>
    );
  }
  if (paper.pattern === 'checks') {
    // gingham: two see-through bands crossing, darker where they meet
    return (
      <pattern id={id} patternUnits="userSpaceOnUse" width={12} height={12} patternTransform={zoom}>
        <rect x={0} y={0} width={6} height={12} fill={ink} opacity={0.3} />
        <rect x={0} y={0} width={12} height={6} fill={ink} opacity={0.3} />
      </pattern>
    );
  }
  if (paper.pattern === 'words') {
    const text = `${(paper.words || 'Happy Birthday').trim()} · `;
    const size = 6;
    const w = Math.max(20, measure(text, { font: WORDS_FONT, sizeMm: size, bold: true, italic: false }));
    const row = size * 1.45;
    // two rows a tile, the second shifted by half a line, so the words read as brickwork
    return (
      <pattern id={id} patternUnits="userSpaceOnUse" width={w} height={row * 2} patternTransform={zoom}>
        {[[0, row * 0.75], [-w / 2, row * 1.75], [w / 2, row * 1.75]].map(([x, y], i) => (
          <text key={i} x={x} y={y} fontFamily={WORDS_FONT} fontWeight={700} fontSize={size} fill={ink}>{text}</text>
        ))}
      </pattern>
    );
  }
  return (
    <pattern id={id} patternUnits="userSpaceOnUse" width={TILE} height={TILE} patternTransform={zoom}>
      {scatter(paper.pattern, ink)}
    </pattern>
  );
}

/** The paper over the sheet's rectangle (page millimetres): its colour, then its pattern. */
export function PaperFill({ paper, id, W, H }: { paper: PaperStyle | undefined; id: string; W: number; H: number }) {
  if (!hasPaper(paper)) return null;
  return (
    <g className="paper-fill" pointerEvents="none">
      <rect x={0} y={0} width={W} height={H} fill={paper.color} />
      {paper.pattern !== 'plain' && <rect x={0} y={0} width={W} height={H} fill={`url(#${id})`} />}
    </g>
  );
}

/** A small swatch of a paper, for the chooser. */
export function PaperSwatch({ paper }: { paper: PaperStyle }) {
  const id = `sw-${paper.pattern}-${paper.color.slice(1)}-${(paper.ink || '').slice(1)}`;
  return (
    <svg viewBox="0 0 30 30" className="paper-swatch" aria-hidden="true">
      <defs><PaperDefs paper={paper} id={id} scale={0.45} /></defs>
      <rect x={0} y={0} width={30} height={30} fill={paper.color} stroke="#c9d1da" strokeWidth={0.6} />
      {paper.pattern !== 'plain' && <rect x={0} y={0} width={30} height={30} fill={`url(#${id})`} />}
    </svg>
  );
}
